import React, { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Select, InputNumber, message } from 'antd';
import chatStore from '../stores/chatStore';
import { useTheme } from '../theme/ThemeContext';
import { normalizeAspectList, buildAspectSelectOptions, getModelAspectValues, aspectPreviewSize } from '../utils/aspectRatios';
import {
    formatSize,
    longEdgeFromTier,
    exceedsMaxAspect,
    normalizePixelSize,
    sizeFromAspect,
    snapToStep,
    getModelResolutionValues,
    getModelNMax,
} from '../utils/imageSize';
import { autoSizePromptTextarea, getPromptMinRows } from '../utils/promptTextarea';

const CUSTOM_RES = '__custom__';
const MAX_CONCURRENT = 3;

const IconCopy = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M5 15V5a2 2 0 012-2h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const IconClear = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
);

const IconSend = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="M5 12h12M13 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconStop = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
);

async function enqueueImageFiles(fileList) {
    const files = Array.from(fileList || []).filter(
        (f) => f && (f.type || '').startsWith('image/')
    );
    if (!files.length) {
        message.warning('Можно прикрепить только изображения');
        return;
    }
    const queue = [...files];
    const workers = Array.from({ length: Math.min(MAX_CONCURRENT, queue.length) }, async () => {
        while (queue.length) {
            const file = queue.shift();
            if (file) {
                // eslint-disable-next-line no-await-in-loop
                await chatStore.uploadAttachment(file);
            }
        }
    });
    await Promise.all(workers);
}

function extractClipboardImages(clipboardData) {
    const files = [];
    const items = Array.from(clipboardData?.items || []);
    for (const item of items) {
        const type = item.type || '';
        if (item.kind === 'file' || type.startsWith('image/')) {
            const file = item.getAsFile();
            if (!file) continue;
            const mime = file.type || type || 'image/png';
            if (!mime.startsWith('image/')) continue;
            const ext = (mime.split('/')[1] || 'png').replace('jpeg', 'jpg');
            files.push(
                new File([file], `paste-${Date.now()}-${files.length}.${ext}`, { type: mime })
            );
        }
    }
    if (!files.length && clipboardData?.files?.length) {
        for (const f of Array.from(clipboardData.files)) {
            if ((f.type || '').startsWith('image/')) files.push(f);
        }
    }
    return files;
}

function AspectRatioThumb({ ratio, isDark, size = 16, className = '' }) {
    const { width, height } = aspectPreviewSize(ratio, size);
    return (
        <span
            className={`inline-flex shrink-0 items-center justify-center ${className}`}
            style={{ width: size, height: size }}
            aria-hidden
        >
            <span
                className={`block rounded-[3px] border ${
                    isDark ? 'border-white/55 bg-white/10' : 'border-black/35 bg-black/[0.06]'
                }`}
                style={{ width, height }}
            />
        </span>
    );
}

const ImageGenForm = observer(({ prompt, onPromptChange }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const taRef = useRef(null);
    const fileRef = useRef(null);
    const lockAspectRef = useRef(true);
    const model = chatStore.imageModels.find((m) => m.id === chatStore.settings.selected_image_model);
    const params = model?.supported_parameters || {};
    const img = chatStore.imageParams;

    const aspectItems = useMemo(
        () => normalizeAspectList(chatStore.settings.aspect_ratios),
        [chatStore.settings.aspect_ratios]
    );

    const modelAspectValues = useMemo(() => getModelAspectValues(params), [params]);

    const aspectOptions = useMemo(
        () =>
            buildAspectSelectOptions({
                userItems: aspectItems,
                modelValues: modelAspectValues,
            }),
        [aspectItems, modelAspectValues]
    );

    const selectableAspectOptions = useMemo(
        () => aspectOptions.filter((o) => !o.disabled),
        [aspectOptions]
    );

    const resolutionValues = useMemo(() => {
        const fromModel = getModelResolutionValues(params);
        if (fromModel.length) return fromModel;
        if (chatStore.provider === 'nanogpt') return ['1K', '2K', '4K'];
        return [];
    }, [params, chatStore.provider]);

    const hasResolutionTiers = resolutionValues.length > 0;

    const aspectId = img.aspectId || selectableAspectOptions[0]?.value || aspectOptions[0]?.value;
    const resolution = hasResolutionTiers
        ? img.resolution || resolutionValues[0] || CUSTOM_RES
        : CUSTOM_RES;
    const width = img.width || 1024;
    const height = img.height || 1024;

    const nMin = Math.max(1, Number(params.n?.min) || 1);
    const nMax = Math.max(nMin, getModelNMax(params));
    const canChooseCount = nMax > 1;
    const nOptions = useMemo(() => {
        if (!canChooseCount) return [1];
        const cap = Math.min(nMax, 10);
        return Array.from({ length: cap - nMin + 1 }, (_, i) => nMin + i);
    }, [canChooseCount, nMin, nMax]);
    const n = Math.min(nMax, Math.max(nMin, Number(img.n) || nMin));

    const selectedAspect = aspectOptions.find((o) => o.value === aspectId);
    const aspectRatio = selectedAspect?.ratio || selectedAspect?.value || img.aspectRatio || '1:1';
    const isCustomRes = resolution === CUSTOM_RES;

    const patchImage = (patch) => {
        chatStore.setImageParams(patch);
    };

    // синхронизация aspectId / resolution с доступными опциями
    useEffect(() => {
        if (!selectableAspectOptions.length) return;
        const current = aspectOptions.find((o) => o.value === aspectId);
        if (!current || current.disabled) {
            const next = selectableAspectOptions[0];
            patchImage({ aspectId: next.value, aspectRatio: next.ratio });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aspectOptions, selectableAspectOptions, aspectId]);

    useEffect(() => {
        if (!hasResolutionTiers) {
            if (resolution !== CUSTOM_RES) {
                patchImage({ resolution: CUSTOM_RES });
            }
            return;
        }
        const hasCurrent = resolutionValues.some(
            (value) => String(value).toLowerCase() === String(resolution).toLowerCase()
        );
        if (!hasCurrent && resolution !== CUSTOM_RES) {
            patchImage({ resolution: resolutionValues[0] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasResolutionTiers, resolutionValues, resolution]);

    useEffect(() => {
        const current = Number(img.n) || 1;
        if (current < nMin || current > nMax) {
            patchImage({ n: Math.min(nMax, Math.max(nMin, current)) });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nMin, nMax, img.n]);

    const autoSize = () => autoSizePromptTextarea(taRef.current);

    useEffect(() => {
        autoSize();
    }, [prompt]);

    const onWidthChange = (v) => {
        lockAspectRef.current = false;
        patchImage({
            width: snapToStep(v),
            resolution: isCustomRes ? resolution : CUSTOM_RES,
        });
    };

    const onHeightChange = (v) => {
        lockAspectRef.current = false;
        patchImage({
            height: snapToStep(v),
            resolution: isCustomRes ? resolution : CUSTOM_RES,
        });
    };

    const normalizeDims = (nextW = width, nextH = height, { notify = false } = {}) => {
        const beforeExceeds = exceedsMaxAspect(nextW, nextH);
        const { width: w, height: h } = normalizePixelSize(nextW, nextH);
        patchImage({ width: w, height: h });
        if (notify && beforeExceeds) {
            message.info(`Соотношение ограничено до 3:1 → ${w}×${h}`);
        }
        return { width: w, height: h };
    };

    const resolutionTier = useMemo(
        () => resolutionValues.map((v) => ({ value: v, label: v })),
        [resolutionValues]
    );

    const onResolutionChange = (v) => {
        if (v !== CUSTOM_RES) {
            lockAspectRef.current = true;
            const size = sizeFromAspect(aspectRatio, longEdgeFromTier(v));
            patchImage({
                resolution: v,
                width: size.width,
                height: size.height,
                aspectRatio,
            });
        } else {
            patchImage({ resolution: v });
        }
    };

    const submit = async () => {
        if (!prompt.trim() || chatStore.streaming) return;
        const body = {};
        const { width: w, height: h } = normalizeDims(width, height, { notify: true });

        if (isCustomRes) {
            if (chatStore.provider === 'nanogpt') {
                const longEdge = Math.max(w, h);
                body.resolution = longEdge >= 3200 ? '4K' : longEdge >= 1600 ? '2K' : '1K';
                if (aspectRatio) body.aspect_ratio = aspectRatio;
            } else {
                const size = formatSize(w, h);
                if (size) body.size = size;
                body.width = w;
                body.height = h;
            }
        } else {
            if (aspectRatio) body.aspect_ratio = aspectRatio;
            if (resolution) body.resolution = resolution;
        }
        if (n) body.n = n;
        const text = prompt.trim();
        onPromptChange('');
        await chatStore.persistChatParams();
        await chatStore.generateImage(text, body);
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
        }
    };

    const onPaste = (e) => {
        const images = extractClipboardImages(e.clipboardData);
        if (!images.length) return;
        e.preventDefault();
        enqueueImageFiles(images);
    };

    const hasPrompt = !!(prompt || '').trim();

    const copyPrompt = async () => {
        if (!hasPrompt) return;
        try {
            await navigator.clipboard.writeText(prompt);
            message.success('Скопировано');
        } catch (e) {
            message.error(e.message || 'Не удалось скопировать');
        }
    };

    const clearPrompt = () => {
        onPromptChange('');
        requestAnimationFrame(autoSize);
    };

    const controlClass = isDark
        ? '[&_.ant-select-selector]:!bg-white/5 [&_.ant-select-selector]:!border-white/10 [&_.ant-select-selection-item]:!text-gray-100 [&_.ant-input-number]:!bg-white/5 [&_.ant-input-number]:!border-white/10 [&_.ant-input-number-input]:!text-gray-100'
        : '';

    const toolBtnClass = `flex h-8 w-8 items-center justify-center rounded-lg transition disabled:cursor-not-allowed disabled:opacity-30 ${
        isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
    }`;

    return (
        <div
            className={`overflow-hidden rounded-2xl border shadow-sm ${
                isDark ? 'border-white/10 bg-[#2f2f2f]' : 'border-black/10 bg-white'
            }`}
        >
            <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                onChange={(e) => {
                    enqueueImageFiles(e.target.files);
                    e.target.value = '';
                }}
            />
            <div className="flex items-end gap-1.5 px-2 py-2 sm:gap-2 sm:px-3 sm:py-2.5">
                <textarea
                    ref={taRef}
                    value={prompt}
                    onChange={(e) => {
                        onPromptChange(e.target.value);
                        autoSizePromptTextarea(e.target);
                    }}
                    onKeyDown={onKeyDown}
                    onPaste={onPaste}
                    rows={getPromptMinRows()}
                    placeholder="Опишите изображение…"
                    className={`bananas-msg-font box-border min-w-0 flex-1 resize-none overflow-y-hidden bg-transparent px-2 py-2 leading-normal outline-none placeholder:opacity-45 sm:py-2.5 ${
                        isDark ? 'text-gray-100' : 'text-gray-900'
                    }`}
                />
                <div className="bananas-btn-scale mb-0.5 shrink-0">
                    {chatStore.streaming ? (
                        <button
                            type="button"
                            onClick={() => chatStore.stop()}
                            title="Остановить"
                            aria-label="Остановить"
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white transition hover:bg-red-500"
                        >
                            <IconStop />
                        </button>
                    ) : (
                        <button
                            type="button"
                            disabled={!prompt.trim()}
                            onClick={submit}
                            title="Создать"
                            aria-label="Создать"
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-35"
                        >
                            <IconSend />
                        </button>
                    )}
                </div>
            </div>

            <div
                className={`flex flex-col gap-2 border-t px-3 py-2.5 sm:flex-row sm:items-center ${
                    isDark ? 'border-white/10' : 'border-black/8'
                }`}
            >
                <div className={`flex flex-wrap items-center gap-2 ${controlClass}`}>
                    <div className="bananas-btn-scale flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            title="Добавить референс-изображение"
                            className={toolBtnClass}
                        >
                            ＋
                        </button>
                        <button
                            type="button"
                            title="Скопировать"
                            disabled={!hasPrompt}
                            onClick={copyPrompt}
                            className={toolBtnClass}
                        >
                            <IconCopy />
                        </button>
                        <button
                            type="button"
                            title="Очистить"
                            disabled={!hasPrompt}
                            onClick={clearPrompt}
                            className={toolBtnClass}
                        >
                            <IconClear />
                        </button>
                    </div>
                    <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:flex-none">
                        <span className="hidden text-[11px] opacity-50 sm:inline">Формат</span>
                        <Select
                            size="middle"
                            value={aspectId}
                            options={aspectOptions.map((opt) => ({
                                value: opt.value,
                                ratio: opt.ratio,
                                disabled: opt.disabled,
                                source: opt.source,
                                available: opt.available,
                                label: (
                                    <span className="flex min-w-0 items-center gap-2">
                                        <AspectRatioThumb ratio={opt.ratio} isDark={isDark} />
                                        <span className="min-w-0 truncate">{opt.label}</span>
                                    </span>
                                ),
                            }))}
                            onChange={(v) => {
                                lockAspectRef.current = true;
                                const opt = aspectOptions.find((o) => o.value === v);
                                const ratio = opt?.ratio || v;
                                if (resolution !== CUSTOM_RES) {
                                    const size = sizeFromAspect(ratio, longEdgeFromTier(resolution));
                                    patchImage({
                                        aspectId: v,
                                        aspectRatio: ratio,
                                        width: size.width,
                                        height: size.height,
                                    });
                                } else {
                                    const size = sizeFromAspect(ratio, Math.max(width, height));
                                    patchImage({
                                        aspectId: v,
                                        aspectRatio: ratio,
                                        width: size.width,
                                        height: size.height,
                                    });
                                }
                            }}
                            className="min-w-[140px] flex-1 sm:flex-none"
                            popupMatchSelectWidth={false}
                            getPopupContainer={() => document.body}
                            popupClassName={`bananas-composer-dropdown ${
                                isDark ? 'bananas-composer-dropdown--dark' : ''
                            }`}
                            optionRender={(option) => {
                                const data = option.data || {};
                                const isUser = data.source === 'user';
                                const optMeta = aspectOptions.find((o) => o.value === data.value) || {};
                                const labelText = optMeta.label || data.ratio || '';
                                return (
                                    <div className="flex min-w-0 items-center justify-between gap-2">
                                        <span className="flex min-w-0 items-center gap-2">
                                            <AspectRatioThumb ratio={data.ratio} isDark={isDark} />
                                            <span className="min-w-0 truncate">
                                                {labelText}
                                                {isUser &&
                                                data.ratio &&
                                                labelText !== data.ratio ? (
                                                    <span className="ml-1 opacity-45">{data.ratio}</span>
                                                ) : null}
                                            </span>
                                        </span>
                                        {isUser ? (
                                            <span
                                                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none ${
                                                    data.available === true
                                                        ? isDark
                                                            ? 'bg-emerald-500/20 text-emerald-300'
                                                            : 'bg-emerald-100 text-emerald-800'
                                                        : isDark
                                                          ? 'bg-white/10 text-white/45'
                                                          : 'bg-black/5 text-black/40'
                                                }`}
                                            >
                                                {data.available === true ? 'своё · ок' : 'своё'}
                                            </span>
                                        ) : null}
                                    </div>
                                );
                            }}
                        />
                        <span
                            className={`shrink-0 text-xs tabular-nums sm:hidden ${
                                isDark ? 'text-white/55' : 'text-black/50'
                            }`}
                            title="Размер генерации"
                        >
                            {(formatSize(width, height) || `${width}x${height}`).replace(
                                'x',
                                ' × '
                            )}
                        </span>
                    </div>
                    {hasResolutionTiers && (
                        <div
                            className={`flex w-full min-w-0 rounded-xl border p-0.5 sm:w-auto ${
                                isDark
                                    ? 'border-white/10 bg-white/5'
                                    : 'border-black/10 bg-black/[0.03]'
                            }`}
                            role="group"
                            aria-label="Качество"
                        >
                            {resolutionTier.map((opt) => {
                                const active = resolution === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => onResolutionChange(opt.value)}
                                        className={`flex-1 rounded-lg px-2.5 py-1.5 text-center text-xs font-medium transition sm:flex-none sm:min-w-[2.75rem] ${
                                            active
                                                ? isDark
                                                    ? 'bg-white/15 text-white'
                                                    : 'bg-white text-gray-900 shadow-sm'
                                                : isDark
                                                  ? 'text-white/55 hover:text-white'
                                                  : 'text-gray-500 hover:text-gray-800'
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                            <button
                                type="button"
                                onClick={() => onResolutionChange(CUSTOM_RES)}
                                className={`hidden flex-1 rounded-lg px-2.5 py-1.5 text-center text-xs font-medium transition sm:block sm:flex-none ${
                                    isCustomRes
                                        ? isDark
                                            ? 'bg-white/15 text-white'
                                            : 'bg-white text-gray-900 shadow-sm'
                                        : isDark
                                          ? 'text-white/55 hover:text-white'
                                          : 'text-gray-500 hover:text-gray-800'
                                }`}
                                title="Свой размер в пикселях"
                            >
                                Своё
                            </button>
                        </div>
                    )}
                    <div className="hidden items-center gap-1 sm:flex">
                        <span className="hidden text-[11px] opacity-50 sm:inline">px</span>
                        <InputNumber
                            size="middle"
                            min={64}
                            max={8192}
                            step={16}
                            value={width}
                            onChange={onWidthChange}
                            onBlur={() => normalizeDims(width, height, { notify: true })}
                            className="w-[88px]"
                            title="Ширина (кратно 16, max 3:1)"
                        />
                        <span className="opacity-40">×</span>
                        <InputNumber
                            size="middle"
                            min={64}
                            max={8192}
                            step={16}
                            value={height}
                            onChange={onHeightChange}
                            onBlur={() => normalizeDims(width, height, { notify: true })}
                            className="w-[88px]"
                            title="Высота (кратно 16, max 3:1)"
                        />
                    </div>
                    {canChooseCount && (
                        <div
                            className={`flex w-full min-w-0 rounded-xl border p-0.5 sm:w-auto ${
                                isDark
                                    ? 'border-white/10 bg-white/5'
                                    : 'border-black/10 bg-black/[0.03]'
                            }`}
                            role="group"
                            aria-label="Количество изображений"
                            title="Сколько изображений сгенерировать"
                        >
                            {nOptions.map((count) => {
                                const active = n === count;
                                return (
                                    <button
                                        key={count}
                                        type="button"
                                        onClick={() => patchImage({ n: count })}
                                        className={`flex-1 rounded-lg px-2.5 py-1.5 text-center text-xs font-medium transition sm:flex-none sm:min-w-[2.25rem] ${
                                            active
                                                ? isDark
                                                    ? 'bg-white/15 text-white'
                                                    : 'bg-white text-gray-900 shadow-sm'
                                                : isDark
                                                  ? 'text-white/55 hover:text-white'
                                                  : 'text-gray-500 hover:text-gray-800'
                                        }`}
                                    >
                                        {count}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default ImageGenForm;
